import {
  SelectTopperItem,
  SelectTopperProps,
} from "design-system/components/toppers/select-topper/select-topper";
import { createContentfulProps } from "frontend/contentful/lib/create-contentful-props";
import { SelectTopperEntry } from "frontend/contentful/schema/toppers";
import { RenderContentfulRichText } from "../utils/render-contentful-rich-text";
import { useContentful } from "frontend/hooks/use-contentful";
import { useSearchDropdowns } from "frontend/hooks/use-search-dropdowns";
import { ChangeEvent, useEffect, useRef, useState } from "react";

interface SelectTopperOptions {
  hiddenFacetKeys?: Array<string>;
  hiddenFacetConditions?: { key: string; value: string };
}

export const createSelectTopperProps = createContentfulProps<
  SelectTopperEntry,
  SelectTopperProps,
  SelectTopperOptions
>(({ entry, options, createEditAttributes }) => {
  const { getEntries } = useContentful();
  const { selectedOptions, onSelectedOptionChange } = useSearchDropdowns();

  const props: SelectTopperProps = {
    title: entry.fields.title || "",
    description: entry.fields.description ? (
      <RenderContentfulRichText document={entry.fields.description} />
    ) : undefined,
    theme: entry.fields.theme,

    editAttributes: {
      title: createEditAttributes({ entry, fieldId: "title" }),
      description: createEditAttributes({ entry, fieldId: "description" }),
    },
  };

  const facets = getEntries(entry.fields.facets);
  const items: Array<SelectTopperItem> = [];
  const prevItems = useRef<Array<SelectTopperItem>>([]);
  const hiddenFacets: Array<string> = options?.hiddenFacetKeys || [];
  const hiddenFacetConditions = options?.hiddenFacetConditions || undefined;

  facets.forEach((facet) => {
    if (facet.fields.field && !hiddenFacets.includes(facet.fields.field)) {
      const facetOptions = [];
      if (facet.fields.terms) {
        facetOptions.push(
          { label: "Select One", value: "" },
          ...facet.fields.terms.map((term) => ({ label: term, value: term })),
        );
      }

      const selectedOption = selectedOptions[facet.fields.field];

      items.push({
        key: facet.fields.field,
        label: facet.fields.label || "",
        options: facetOptions,

        selectedOption: selectedOption || "",
        onChange: (event: ChangeEvent<HTMLSelectElement>) => {
          onSelectedOptionChange(facet.fields.field!, event.target.value);
        },

        editAttributes: {
          label: createEditAttributes({ entry, fieldId: "label" }),
        },
      });
    }
  });

  const [selectedItems, setSelectedItems] = useState<Array<SelectTopperItem>>(
    [],
  );
  props.items = selectedItems;

  useEffect(() => {
    const url = new URL(window.location.href);
    let selectedOption: string | undefined = undefined;
    facets.forEach((facet) => {
      let display = true;
      if (facet.fields.field && hiddenFacets.includes(facet.fields.field)) {
        const params = url.searchParams.get(facet.fields.field) || undefined;
        const conditionParams =
          (hiddenFacetConditions?.key &&
            url.searchParams.get(hiddenFacetConditions?.key)?.toLowerCase()) ||
          undefined;
        // Hide facet if hidden facet is set and the condition is not met
        if (
          !params &&
          conditionParams !== hiddenFacetConditions?.value?.toLowerCase()
        ) {
          display = false;
        }
      }
      if (facet.fields.field && display) {
        const facetOptions = [{ label: "Select One", value: "" }];
        if (facet.fields.terms) {
          facetOptions.push(
            ...facet.fields.terms.map((term) => ({ label: term, value: term })),
          );
        }

        selectedOption =
          selectedOptions[facet.fields.field] ||
          prevItems.current.filter((item) => item.key === facet.fields.field)[0]
            ?.selectedOption;

        if (!Object.keys(selectedOptions).length && facet.fields.field) {
          selectedOption = url.searchParams.get(facet.fields.field) || "";
          const selectedFacetValue = facetOptions.find(
            (option) => option.value === selectedOption,
          );
          // If the selected option is not found, add an extra option to select it
          if (selectedOption && !selectedFacetValue) {
            facetOptions.push({ label: selectedOption, value: selectedOption });
          }
        }

        // Sort the facet options
        facetOptions.sort((a, b) => {
          const aIsEmpty = a.value === "";
          const bIsEmpty = b.value === "";

          // 1. Empty strings come first
          if (aIsEmpty && !bIsEmpty) return -1;
          if (bIsEmpty && !aIsEmpty) return 1;

          const aNum = Number(a.value);
          const bNum = Number(b.value);
          const aIsNumber = !isNaN(aNum);
          const bIsNumber = !isNaN(bNum);

          // 2. If both are numbers → sort descending
          if (aIsNumber && bIsNumber) {
            return bNum - aNum;
          }

          // 3. If both are not numbers → sort ascending (case-insensitive)
          if (!aIsNumber && !bIsNumber) {
            return a.value.localeCompare(b.value, undefined, {
              sensitivity: "base",
            });
          }

          // 4. Mixed: numbers after strings
          return aIsNumber ? 1 : -1;
        });

        // Check if the item already exists in the items array
        const existingItem = items.find(
          (item) => item.key === facet.fields.field,
        );
        // If it doesn't exist, push a new item
        if (!existingItem) {
          // Create a new item
          items.push({
            key: facet.fields.field,
            label: facet.fields.label || "",
            options: facetOptions,
            selectedOption: selectedOption || "",
            onChange: (event: ChangeEvent<HTMLSelectElement>) => {
              onSelectedOptionChange(facet.fields.field!, event.target.value);
            },

            editAttributes: {
              label: createEditAttributes({ entry, fieldId: "label" }),
            },
          });
        } else {
          // If it exists, update the selectedOption
          existingItem.selectedOption = selectedOption || "";
          existingItem.options = facetOptions;
          existingItem.onChange = (event: ChangeEvent<HTMLSelectElement>) => {
            onSelectedOptionChange(facet.fields.field!, event.target.value);
          };
        }
      }
    });
    // setSelectedItems when items change
    if (items.length > 0) {
      items.forEach((item) => {
        if (Object.keys(selectedOptions).length) {
          item.selectedOption = selectedOptions[item.key] || "";
        } else {
          item.selectedOption = url.searchParams.get(item.key) || "";
        }
      });
    }

    if (
      JSON.stringify(items) !== JSON.stringify(prevItems.current) ||
      prevItems.current.length === 0
    ) {
      setSelectedItems(items);
      prevItems.current = items;
    }
  }, [
    facets,
    hiddenFacets,
    hiddenFacetConditions,
    selectedOptions,
    onSelectedOptionChange,
    setSelectedItems,
  ]);

  return props;
});
